import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { confirm, haptic } from '../lib/telegram';
import AmountInput from '../components/AmountInput';
import { CATEGORIES as DEFAULT_CATEGORIES, type Currency, type Entry } from '../lib/types';
import { parseAmountInput, toDatetimeLocal, datetimeLocalToISO } from '../lib/format';

interface Props {
  kind: 'income' | 'expense';
  editing?: Entry;          // when present, edit instead of create
  onDone: () => void;
  onBack: () => void;
}

export default function AddEntry({ kind, editing, onDone, onBack }: Props) {
  const editAmount = editing ? Math.abs(Number(editing.amount)) : 0;
  const [currency, setCurrency] = useState<Currency>(editing?.currency ?? 'SYP');
  const [amount, setAmount] = useState(editing ? String(editAmount) : '');
  const [category, setCategory] = useState<string>(editing?.category ?? DEFAULT_CATEGORIES[0]);
  const [note, setNote] = useState(editing?.note ?? '');
  const [occurredAt, setOccurredAt] = useState(
    toDatetimeLocal(editing ? new Date(editing.occurred_at) : new Date()),
  );
  const [photoUrl, setPhotoUrl] = useState<string | null>(editing?.photo_url ?? null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [categories, setCategories] = useState<string[]>([...DEFAULT_CATEGORIES]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refundAmt, setRefundAmt] = useState('');
  const [refundFrom, setRefundFrom] = useState('');
  const [refundBusy, setRefundBusy] = useState(false);

  async function applyRefund() {
    if (!editing) return;
    const amt = parseAmountInput(refundAmt);
    if (amt === null) { setErr('Refund amount must be positive'); return; }
    setErr(null);
    setRefundBusy(true);
    try {
      const updated = await api.refundEntry(editing.id, amt, refundFrom.trim() || undefined);
      // Update local form to reflect the new (decremented) amount and audit note.
      setAmount(String(Math.abs(Number(updated.amount))));
      setNote(updated.note ?? '');
      setRefundAmt('');
      setRefundFrom('');
      haptic('success');
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'refund failed');
    } finally {
      setRefundBusy(false);
    }
  }

  async function onPickPhoto(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    setErr(null);
    try {
      const small = await downscaleImage(file);
      const url = await api.uploadPhoto(small);
      setPhotoUrl(url);
      haptic('success');
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'photo upload failed');
    } finally {
      setPhotoBusy(false);
    }
  }

  useEffect(() => {
    api.listCategories()
      .then((r) => {
        setCategories(r.categories);
        // Make sure selected category exists in the list; otherwise keep editing.category if present, else first.
        if (editing?.category && !r.categories.includes(editing.category)) {
          setCategories([editing.category, ...r.categories]);
        } else if (!r.categories.includes(category)) {
          setCategory(r.categories[0]);
        }
      })
      .catch(() => { /* keep defaults */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(ev: React.FormEvent) {
    ev.preventDefault();
    const amt = parseAmountInput(amount);
    if (amt === null) { setErr('Enter a valid positive amount'); return; }
    setErr(null);
    setBusy(true);
    try {
      if (editing) {
        await api.edit(editing.id, {
          amount: amt,
          currency,
          category: kind === 'expense' ? category : null,
          note: note.trim() || null,
          occurred_at: datetimeLocalToISO(occurredAt),
          photo_url: photoUrl,
        });
      } else {
        await api.create({
          kind,
          currency,
          amount: amt,
          category: kind === 'expense' ? category : null,
          note: note.trim() || null,
          occurred_at: datetimeLocalToISO(occurredAt),
          photo_url: photoUrl,
        });
      }
      haptic('success');
      onDone();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!editing) return;
    if (!(await confirm('Delete this entry?'))) return;
    setBusy(true);
    try {
      await api.remove(editing.id);
      haptic('success');
      onDone();
    } catch (e) {
      haptic('error');
      setErr(e instanceof Error ? e.message : 'delete failed');
    } finally {
      setBusy(false);
    }
  }

  const title = editing ? `Edit ${kind}` : (kind === 'income' ? 'Add income' : 'Add expense');

  return (
    <>
      <div className="header">
        <button className="back" onClick={onBack}>← Back</button>
        <h1>{title}</h1>
      </div>
      {err && <div className="error">{err}</div>}

      <form onSubmit={submit}>
        <div className="card">
          <label className="label" style={{ marginTop: 0 }}>Currency</label>
          <div className="segmented">
            <button type="button" className={currency === 'SYP' ? 'active' : ''} onClick={() => setCurrency('SYP')}>SYP</button>
            <button type="button" className={currency === 'USD' ? 'active' : ''} onClick={() => setCurrency('USD')}>USD</button>
          </div>

          <label className="label">Amount</label>
          <AmountInput value={amount} onChange={setAmount} currency={currency} autoFocus />

          {kind === 'expense' && (
            <>
              <label className="label">Category</label>
              <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </>
          )}

          <label className="label">Note (optional)</label>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. coffee with Sami" />

          <label className="label">Date &amp; time</label>
          <input
            className="input"
            type="datetime-local"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
          />

          <label className="label">Receipt photo (optional)</label>
          {photoUrl ? (
            <div className="photo-preview">
              <img src={photoUrl} alt="receipt" />
              <button type="button" className="btn btn-danger" onClick={() => setPhotoUrl(null)}>
                Remove photo
              </button>
            </div>
          ) : (
            <label className="btn btn-secondary" style={{ cursor: 'pointer' }}>
              {photoBusy ? 'Uploading…' : '📷 Add receipt photo'}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                hidden
                disabled={photoBusy}
                onChange={onPickPhoto}
              />
            </label>
          )}
        </div>

        <button type="submit" className="btn" disabled={busy}>
          {busy ? 'Saving…' : (editing ? 'Save changes' : 'Save')}
        </button>

        {editing && (
          <button type="button" className="btn btn-danger" style={{ marginTop: 8 }} onClick={handleDelete} disabled={busy}>
            Delete entry
          </button>
        )}
      </form>

      {editing && kind === 'expense' && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="section-title">💰 Receive a refund</div>
          <p className="muted small" style={{ marginTop: 0 }}>
            Paid for the group and someone paid you back? Apply it here — reduces this
            entry's amount and your category total. You can do this multiple times.
          </p>
          <label className="label">Refund amount ({currency})</label>
          <AmountInput value={refundAmt} onChange={setRefundAmt} currency={currency} />
          <label className="label">From (optional)</label>
          <input
            className="input"
            value={refundFrom}
            onChange={(e) => setRefundFrom(e.target.value)}
            placeholder="e.g. Sami"
            maxLength={60}
          />
          <button
            type="button"
            className="btn btn-secondary"
            style={{ marginTop: 12 }}
            disabled={refundBusy || !refundAmt}
            onClick={applyRefund}
          >
            {refundBusy ? 'Applying…' : '− Apply refund'}
          </button>
        </div>
      )}
    </>
  );
}

// Downscale a phone photo before upload to keep it well under the 5 MB cap.
async function downscaleImage(file: File, maxDim = 1280, quality = 0.8): Promise<File> {
  if (!file.type.startsWith('image/')) return file;
  try {
    const img = await new Promise<HTMLImageElement>((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = rej;
      i.src = URL.createObjectURL(file);
    });
    const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 1_000_000) {
      URL.revokeObjectURL(img.src);
      return file;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, 'image/jpeg', quality));
    URL.revokeObjectURL(img.src);
    return blob ? new File([blob], 'receipt.jpg', { type: 'image/jpeg' }) : file;
  } catch {
    return file;
  }
}
